import React, { useEffect, useState } from "react";
import {
  Box,
  Grid2 as Grid,
  Typography,
  TextField,
  Stack,
  Avatar,
  Paper,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Button,
  SelectChangeEvent,
  useTheme,
  Chip
} from "@mui/material";
import AttachmentUpload from "./AttachmentUpload";
import DatePickerComponent from "../CommonComponents/CustomDatePicker";
import dayjs from "dayjs";
import ServiceRequestEditLogs from "./ServiceRequestEditLogs";
import { CalendarToday, Cancel, Handyman, MeetingRoom } from "@mui/icons-material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Actions } from "../../Redux/Reducer/rootReducer";
import { createServiceRequestAPIStatus, fetchServiceRequestUpdates, singleServiceRequest, updateServiceRequestAPIStatus } from "../../Redux/selectors";
import { APICallStatus, Priority, ServiceRequestStatus, ServiceRequestTypes, UserType, ViewServiceRequestData } from "../../Types/UnitFeatureTypes";
import DropdownSelection from "../CommonComponents/DropdownSelection";
import CommentSection from "./CommentSection";
import { useAuth } from "../../context/authContext";
import Editor from "../TextEditor/Editor";
import { useApp } from "../../context/appContext";

const ServiceRequestView: React.FC = () => {
  const isDisabeld = true;
  const [tab, setTab] = useState(0);
  const [disableSaveButton, setDisableSaveButton] = useState<boolean>(true);
  const theme = useTheme();
  const navigate = useNavigate();
  const updates = useSelector(fetchServiceRequestUpdates);
  const editServiceRequestAPI = useSelector(updateServiceRequestAPIStatus)
  const [serviceRequestData, setServiceRequestData] = useState<ViewServiceRequestData>({
    title: '',
    description: '',
    type: ServiceRequestTypes.MAINTENANCE,
    status: ServiceRequestStatus.CANCELLED,
    priority: Priority.CRITICAL,
    reporter_id: 1,
    reporter_unit_id: 1,
    user_first_name: '',
    user_last_name: '',
    unit_number: '',
    created_on: new Date(),
    estimated_resolution_date: undefined,
    has_permission_to_enter: false,
  });
  // const [showEditableDescription, setShowEditableDescription] = useState(false);
  // const [showEditButton, setShowEditButton] = useState(false);
  const currentRequestData = useSelector(singleServiceRequest) as ViewServiceRequestData;
  const [showDateSaveButton, setShowDateSaveButton] = useState(false);
  const dispatch = useDispatch();
  const location = useLocation();

  const { user } = useAuth();
  const IS_MANAGER = user?.type === UserType.MANAGER;
  const { setSnackbar } = useApp();


  useEffect(() => {
    if (editServiceRequestAPI === APICallStatus.SUCCESS) {
      dispatch(Actions.fetch.serviceRequestActions.request(
        { id: location.state.id }
      ));
      dispatch(Actions.fetchServiceRequestUpdates.serviceRequestUpdatesActions.request({
        service_request_id: location.state.id || ''
      }))
      dispatch(Actions.serviceRequest.resetStatusRequestAction());
      setSnackbar(true, 'Service Request Updated Successfully', 'success');
    } else if (editServiceRequestAPI === APICallStatus.FAILURE) {
      setSnackbar(true, 'Failed to Update Service Request', 'error');
      dispatch(Actions.serviceRequest.resetStatusRequestAction());
    }
  }, [editServiceRequestAPI]);

  useEffect(() => {
    if (!location?.state?.id) return;
    if (currentRequestData && !Array.isArray(currentRequestData)) return;
    dispatch(Actions.fetch.serviceRequestActions.request(
      { id: location.state.id }
    ));
    dispatch(Actions.fetchServiceRequestUpdates.serviceRequestUpdatesActions.request({
      service_request_id: location.state.id || ''
    }))
  }, []);

  useEffect(() => {
    if (currentRequestData && Array.isArray(currentRequestData) && currentRequestData.length > 0) {
      setServiceRequestData({
        title: currentRequestData[0].title,
        description: currentRequestData[0].description,
        priority: currentRequestData[0].priority,
        status: currentRequestData[0].status,
        type: currentRequestData[0].type,
        user_first_name: currentRequestData[0].user_first_name,
        user_last_name: currentRequestData[0].user_last_name,
        unit_number: currentRequestData[0].unit_number,
        estimated_resolution_date: currentRequestData[0].estimated_resolution_date,
        created_on: currentRequestData[0].created_on,
        reporter_id: currentRequestData[0].reporter_id,
        reporter_unit_id: currentRequestData[0].reporter_unit_id,
        has_permission_to_enter: currentRequestData[0].has_permission_to_enter
      });
    } else if (currentRequestData && !Array.isArray(currentRequestData)) {
      setServiceRequestData({
        title: currentRequestData.title,
        description: currentRequestData.description,
        priority: currentRequestData.priority,
        status: currentRequestData.status,
        type: currentRequestData.type,
        user_first_name: currentRequestData.user_first_name,
        user_last_name: currentRequestData.user_last_name,
        unit_number: currentRequestData.unit_number,
        estimated_resolution_date: currentRequestData.estimated_resolution_date,
        created_on: currentRequestData.created_on,
        reporter_id: currentRequestData.reporter_id,
        reporter_unit_id: currentRequestData.reporter_unit_id,
        has_permission_to_enter: currentRequestData.has_permission_to_enter
      });
    }
  }, [currentRequestData]);

  useEffect(() => {
    if (Array.isArray(currentRequestData)) return
    if (
      currentRequestData.title === serviceRequestData.title &&
      currentRequestData.description === serviceRequestData.description &&
      currentRequestData.priority === serviceRequestData.priority &&
      currentRequestData.status === serviceRequestData.status &&
      currentRequestData.type === serviceRequestData.type &&
      currentRequestData.estimated_resolution_date === serviceRequestData.estimated_resolution_date
    ) {
      setDisableSaveButton(true);
    } else {
      setDisableSaveButton(false);
    }
  }, [serviceRequestData])

  const handleTabChange = (currentTab: number) => {
    setTab(currentTab);
  };

  const dropdownSXObject = {
    mt: 1.5,
    borderRadius: 20,
    padding: 0,
    '&:before': {
        border: 'none',
    },
    '&:after': {
        border: 'none',
    },
    '& .MuiSelect-select': {
        padding: 0,
        color: 'primary.main',
    },
  };

  const handleEditServiceRequest = () => {
    if (!location?.state?.id) return;
    dispatch(Actions.serviceRequest.editServiceRequestAction.request({
      serviceRequestData,
      id: location?.state?.id
    }));
  }

  const handleChangeDropdown = (event: SelectChangeEvent<any>, field: string) => {
    setServiceRequestData((prevData) => ({
      ...prevData,
      [field]: event.target.value
    }));
  }

  const handleUnitClick = () => {
    if (IS_MANAGER) {
      navigate('/unitFile/', { state: { unit_id: serviceRequestData.reporter_unit_id } });
    } else {
      navigate('/dashboard')
    }
  }

  const handleUserClick = () => {
    // navigate('/user/', { state: { user_id: serviceRequestData.reporter_id } });
    console.log('User Clicked');
  }

  const getStatus = (status: ServiceRequestStatus) => {
    switch (status) {
      case ServiceRequestStatus.NEW:
        return {color: theme.palette.primary.main, label: 'New'}
      case ServiceRequestStatus.PENDING:
        return {color: theme.palette.warning.main, label: 'Pending'}
      case ServiceRequestStatus.IN_PROGRESS:
        return {color: 'blue', label: 'In Progress'}
      case ServiceRequestStatus.COMPLETED:
        return {color: theme.palette.success.main, label: 'Completed'}
      case ServiceRequestStatus.CANCELLED:
        return {color: theme.palette.error.main, label: 'Cancelled'}
      default:
        return {color: 'black', label: 'Unknown'}
    }
  }

  const getDataChip = <T extends string>(title: string, dataFunc: (status: T) => { color: string; label: string; }, data: T) => {
    return (
      <>
        <Typography variant="subtitle1" fontWeight="bold">{title}</Typography>
        <Chip
          label={dataFunc(data).label}
          sx={{
            backgroundColor: dataFunc(data).color,
            color: 'white',
            borderRadius: 20
          }}
        />
      </>
    )
  }

  const getPriority = (priority: Priority) => {
    switch (priority) {
      case Priority.LOWEST:
        return {color: 'grey', label: 'Lowest'}
      case Priority.LOW:
        return {color: 'blue', label: 'Low'}
      case Priority.MEDIUM:
        return {color: theme.palette.warning.main, label: 'Medium'}
      case Priority.HIGH:
        return {color: '#953271', label: 'High'}
      case Priority.CRITICAL:
        return {color: theme.palette.error.main, label: 'Highest'}
      default:
        return {color: 'black', label: 'Unknown'}
    }
  }

  const getType = (type: ServiceRequestTypes) => {
    switch (type) {
      case ServiceRequestTypes.GENERAL:
        return {color: 'black', label: 'General'}
      case ServiceRequestTypes.COMPLAINTS:
        return {color: theme.palette.warning.main, label: 'Complaints'}
      case ServiceRequestTypes.FEEDBACK:
        return {color: 'grey', label: 'Feedback'}
      case ServiceRequestTypes.FOB_REQUEST:
        return {color: theme.palette.primary.main, label: 'Fob Request'}
      case ServiceRequestTypes.MAINTENANCE:
        return {color: theme.palette.secondary.main, label: 'Maintenance'}
      case ServiceRequestTypes.PARKING_ISSUE:
        return {color: '#953271', label: 'Parking Issue'}
      case ServiceRequestTypes.TENANT_RELATED:
        return {color: theme.palette.info.main, label: 'Tenant Related'}
      case ServiceRequestTypes.EMERGENCY:
        return {color: theme.palette.error.main, label: 'Emergency'}
      case ServiceRequestTypes.OTHER:
        return {color: 'grey', label: 'Other'}
      default:
        return {color: 'black', label: 'Unknown'}
    }
  }

  return (
      <>
        <Paper elevation={3} sx={{mb: 2, padding: 2, borderRadius: 3, display: 'flex', justifyContent: 'space-between'}}>
          <Box color='primary.main' sx={{display: 'flex', flexDirection: 'row', alignItems: 'center', }}>
              <Handyman sx={{mr:1, height: 40, width: 40}} />
              <Typography variant="h5">Service Request</Typography>
          </Box>
        </Paper>
        <Grid container spacing={3}>
            <Grid size={{xs: 12, md: 4}}>
              <Paper elevation={5} sx={{
                  padding: 4,
                  borderRadius: 3, 
                  minHeight: '34em',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                }}>

                {IS_MANAGER ? <DropdownSelection 
                  dropDownLabel="Status"
                  showHeader
                  showChip
                  currentValue={serviceRequestData.status}
                  sxObject={dropdownSXObject}
                  selectionValues={[
                    { value: ServiceRequestStatus.NEW, label: 'New', color: theme.palette.primary.main },
                    { value: ServiceRequestStatus.PENDING, label: 'Pending', color: theme.palette.warning.main },
                    { value: ServiceRequestStatus.IN_PROGRESS, label: 'In Progress', color: 'blue' },
                    { value: ServiceRequestStatus.COMPLETED, label: 'Completed', color: theme.palette.success.main },
                    { value: ServiceRequestStatus.CANCELLED, label: 'Cancelled', color: theme.palette.error.main },
                  ]}
                  handleOnChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => handleChangeDropdown(event, 'status')}
                />: 
                  getDataChip('Status', getStatus, serviceRequestData.status)
                }

                {IS_MANAGER ? <DropdownSelection
                  dropDownLabel="Priority"
                  showHeader
                  showChip
                  currentValue={serviceRequestData.priority}
                  sxObject={dropdownSXObject}
                  selectionValues={[
                    { value: Priority.LOWEST, label: 'Lowest', color: 'grey' },
                    { value: Priority.LOW, label: 'Low', color: 'blue' },
                    { value: Priority.MEDIUM, label: 'Medium', color: theme.palette.warning.main },
                    { value: Priority.HIGH, label: 'High', color: '#953271' },
                    { value: Priority.CRITICAL, label: 'Critical', color: theme.palette.error.main },
                  ]}
                  handleOnChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => handleChangeDropdown(event, 'priority')}
                />: 
                  getDataChip('Priority', getPriority, serviceRequestData.priority)
                }

                {/* <DropdownSelection
                  dropDownLabel="Type"
                  showHeader
                  showChip
                  currentValue={serviceRequestData.type}
                  sxObject={dropdownSXObject}
                  selectionValues={[
                    { value: ServiceRequestTypes.GENERAL, label: 'General', color: 'black' },
                    { value: ServiceRequestTypes.COMPLAINTS, label: 'Complaints', color: theme.palette.warning.main },
                    { value: ServiceRequestTypes.FEEDBACK, label: 'Feedback', color: 'grey' },
                    { value: ServiceRequestTypes.FOB_REQUEST, label: 'Fob Request', color: theme.palette.primary.main },
                    { value: ServiceRequestTypes.MAINTENANCE, label: 'Maintenance', color: theme.palette.secondary.main },
                    { value: ServiceRequestTypes.PARKING_ISSUE, label: 'Parking Issue', color: '#953271' },
                    { value: ServiceRequestTypes.TENANT_RELATED, label: 'Tenant Related', color: theme.palette.info.main },
                    { value: ServiceRequestTypes.EMERGENCY, label: 'Emergency', color: theme.palette.error.main },
                    { value: ServiceRequestTypes.OTHER, label: 'Other', color: 'grey' },
                  ]}
                  handleOnChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => handleChangeDropdown(event, 'type')}
                /> */}

                {getDataChip('Type', getType, serviceRequestData.type)}

                <Typography variant="subtitle1" fontWeight="bold">Reporter</Typography>
                <Tooltip title={`View ${serviceRequestData.user_first_name}`}>
                  <Stack sx={{ borderRadius: 3, padding: 0.5, '&:hover': { cursor: 'pointer', backgroundColor: 'grey.100'}}} onClick={handleUserClick} direction="row" spacing={1} alignItems="center">
                    <Avatar alt={serviceRequestData.user_first_name + " " + serviceRequestData.user_last_name} src="/path/to/avatar.jpg" />
                    <Typography variant="body1">{serviceRequestData.user_first_name  + " " + serviceRequestData.user_last_name}</Typography>
                  </Stack>
                </Tooltip>

                <Typography variant="subtitle1" fontWeight="bold">Unit</Typography>
                <Tooltip title="View Unit">
                  <Stack sx={{ borderRadius: 3, padding: 0.5, '&:hover': { cursor: 'pointer', backgroundColor: 'grey.100'}}} onClick={handleUnitClick} direction="row" spacing={1} alignItems="center">
                    <MeetingRoom sx={{height: 40, width: 40}} />
                    <Typography variant="body1">{serviceRequestData.unit_number}</Typography>
                  </Stack>
                </Tooltip>

                <Typography variant="subtitle1" fontWeight="bold">Estimated Resolution Date</Typography>
                  {IS_MANAGER ? <Stack direction="row" alignItems="center" justifyContent="space-between">

                  {(serviceRequestData.estimated_resolution_date ? (
                    <DatePickerComponent
                      labelValue=""
                      selectedDate={dayjs(serviceRequestData.estimated_resolution_date)}
                      setSelectedDate={(newDate) => setServiceRequestData({ ...serviceRequestData, estimated_resolution_date: newDate?.toISOString() })}
                      minDate={dayjs()}
                    />
                  ) : (
                    <Button variant='contained' color='primary' onClick={() => {
                    setServiceRequestData({ ...serviceRequestData, estimated_resolution_date: dayjs().toISOString() });
                    setShowDateSaveButton(true);
                    }}>
                    Add Date
                    </Button>
                  ))}
                  {showDateSaveButton && 
                  <Tooltip title="Cancel"><IconButton sx={{ ml: 1, '&:hover': {
                    color: 'error.main'
                  }}} size='small' onClick={() => {
                    setServiceRequestData({ ...serviceRequestData, estimated_resolution_date: undefined });
                    setShowDateSaveButton(false);
                  }}>
                    <Cancel />
                  </IconButton></Tooltip>}
                  </Stack> :
                  <Stack direction="row" alignItems="center">
                    {serviceRequestData.estimated_resolution_date && <CalendarToday sx={{ mr: 2 }} />}
                    <Typography variant="body1">{serviceRequestData.estimated_resolution_date ? dayjs(serviceRequestData.estimated_resolution_date).toDate().toDateString() : 'No Resolution Date Set'}</Typography>
                  </Stack>
                  }
              </Paper>
            </Grid>
            <Grid size={{xs: 12, md: 8 }}>
              <Paper elevation={5} sx={{padding: 4, mb: 2, borderRadius: 3}}>
                <Box marginBottom={3}>
                  <TextField
                    fullWidth
                    value={serviceRequestData.title}
                    variant="standard"
                    // onChange={(e) => setServiceRequestData((prev) => ({ ...prev, title: e.target.value }))}
                    slotProps={{
                      input: {
                        disableUnderline: true,
                        style: {
                          fontSize: '2rem',
                          fontWeight: 'bold',
                        },
                        readOnly: true,
                      },
                    }}
                    sx={{
                      '&:hover': {
                        backgroundColor: isDisabeld ? '' : 'grey.100',
                      }
                    }}
                  />
                </Box>

                <Tabs 
                  variant="scrollable"
                  value={tab} onChange={(event, newValue) => handleTabChange(newValue)}>
                  <Tab sx={{flexGrow: 1}} label="About" />
                  <Tab sx={{flexGrow: 1}} label="Updates" />
                  <Tab sx={{flexGrow: 1}} label="Attachments" />
                  {/* <Tab sx={{flexGrow: 1}} label="Edit Logs" /> */}
                </Tabs>
                {tab === 0 && (
                  <>
                  <Stack direction="row" alignItems="center" justifyContent={'space-between'} sx={{ mt: 2, mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">Description</Typography>
                    <Tooltip title={serviceRequestData.has_permission_to_enter ? 'Management has permission to access the unit' : 'Management doesn\'t have permission to access the unit'}>
                      <Stack direction="row" alignItems="center">
                        <Typography variant="subtitle1" fontWeight="bold">Access</Typography>
                        <Chip 
                          label={serviceRequestData.has_permission_to_enter ? 'Yes' : 'No'}
                          sx={{
                            ml: 2,
                            backgroundColor: serviceRequestData.has_permission_to_enter ? theme.palette.success.main : theme.palette.error.main,
                            color: 'white',
                            borderRadius: 20
                          }}
                        />
                      </Stack>
                    </Tooltip>
                  </Stack>
                  
                  {/* <Stack sx={{ mt:2, mb:1 }} direction="row" spacing={2} onMouseLeave={() => setShowEditButton(false)} onMouseEnter={() => setShowEditButton(true)}>
                    <Typography variant="h6" fontWeight="bold">Description</Typography>
                    {!showEditableDescription && !isDisabeld && <IconButton size='small' sx={{
                      display: showEditButton ? 'flex' : 'none',
                      animation: 'fadeIn 0.3s',
                      borderRadius: 0,
                      backgroundColor: 'grey.100',
                      padding: 0.5,
                      paddingLeft: 1,
                      paddingRight: 1,
                    }}
                    onClick={handleEditOnClick}
                    >
                      <Edit sx={{mr:1}} /> Edit
                    </IconButton>}
                  </Stack> */}
                  <Editor
                      value={serviceRequestData.description}
                      isReadOnly={true}
                      placeholder=""
                      error={false}
                      onChange={() => {}}
                    />
                  {/* {!showEditableDescription ? 
                  <LexicalRichText
                    value={serviceRequestData.description}
                    isReadOnly={true}
                    placeholder=""
                    error={false}
                    onChange={() => {}}
                  />
                    :
                    <>

                      <LexicalRichText
                        value={serviceRequestData.description}
                        placeholder='Please write the details of the description here.'
                        error={false}
                        // helperText={errors.description}
                        onChange={(value) => setServiceRequestData((prev) => ({ ...prev, description: value }))}
                      />

                      <CardActions sx={{ justifyContent: 'flex-end' }}>
                        <Tooltip title="Save Changes">
                        <IconButton size='small' onClick={() => setShowEditableDescription(false)}>
                          <Check />
                        </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                        <IconButton size='small' onClick={() => setShowEditableDescription(false)}>
                          <Clear />
                        </IconButton>
                        </Tooltip>
                      </CardActions>
                    </>
                  } */}
                  </>)}
                {tab === 1 && <CommentSection previousComments={updates}  />}
                {tab === 2 && <AttachmentUpload />}
                {tab === 3 && <ServiceRequestEditLogs />}
              </Paper>
              {IS_MANAGER && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button onClick={handleEditServiceRequest} disabled={disableSaveButton} variant='contained'>Save Changes</Button>
              </Box>}
            </Grid>
        </Grid>
      </>
  );
};

export default ServiceRequestView;
